export interface UIComponent {
  type: string;
  data: Record<string, unknown>;
}

// Matches ```ui-component fences (labeled) and plain ``` fences (unlabeled)
const LABELED_FENCE_RE = /```ui-component[ \t]*\r?\n([\s\S]*?)\r?\n[ \t]*```/g;
const ANY_FENCE_RE = /```(?:\w+)?[ \t]*\r?\n([\s\S]*?)\r?\n[ \t]*```/g;

function tryParseComponent(jsonStr: string): UIComponent | null {
  try {
    const parsed: unknown = JSON.parse(jsonStr);
    const p = parsed as Record<string, unknown>;
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      typeof p['type'] === 'string' &&
      p['data'] !== null &&
      typeof p['data'] === 'object'
    ) {
      return parsed as UIComponent;
    }
  } catch (_) {
    // not valid component JSON
  }
  return null;
}

export type AgentResponseSegment =
  | { type: 'text'; value: string }
  | { type: 'component'; value: UIComponent };

interface FenceSpan {
  start: number;
  end: number;
  component: UIComponent | null;
}

function cleanTextChunk(chunk: string): string {
  return chunk.replace(/\n{3,}/g, '\n\n').trim();
}

export function parseAgentResponse(raw: string): AgentResponseSegment[] {
  const labeledSpans: FenceSpan[] = [...raw.matchAll(LABELED_FENCE_RE)].map((match) => {
    const start = match.index ?? 0;
    return { start, end: start + match[0].length, component: tryParseComponent(match[1] ?? '') };
  });

  let foundComponent = labeledSpans.some((span) => span.component !== null);

  // Fallback for a model that forgot the `ui-component` label. Scan a
  // version of `raw` with every labeled span blanked out (same length, so
  // indices still line up) rather than `raw` itself — otherwise a labeled
  // fence's own closing ``` can be mistaken for the opening of a bogus plain
  // fence that swallows everything up to (and including) the next real
  // fence's opening marker, hiding it from this scan entirely.
  // Any plain fence beyond the first accepted component is still stripped
  // (component: null) rather than left as visible text — a raw JSON blob is
  // never acceptable chat output, whether or not it ends up rendered as a
  // card.
  let masked = raw;
  for (const span of labeledSpans) {
    masked = masked.slice(0, span.start) + ' '.repeat(span.end - span.start) + masked.slice(span.end);
  }

  const plainSpans: FenceSpan[] = [];
  for (const match of masked.matchAll(ANY_FENCE_RE)) {
    const parsed = tryParseComponent(match[1] ?? '');
    if (!parsed) continue;
    const start = match.index ?? 0;
    plainSpans.push({ start, end: start + match[0].length, component: foundComponent ? null : parsed });
    foundComponent = true;
  }

  const spans = [...labeledSpans, ...plainSpans].sort((a, b) => a.start - b.start);

  const segments: AgentResponseSegment[] = [];
  let cursor = 0;
  for (const span of spans) {
    const textChunk = cleanTextChunk(raw.slice(cursor, span.start));
    if (textChunk) segments.push({ type: 'text', value: textChunk });
    if (span.component) segments.push({ type: 'component', value: span.component });
    cursor = span.end;
  }
  const trailingChunk = cleanTextChunk(raw.slice(cursor));
  if (trailingChunk) segments.push({ type: 'text', value: trailingChunk });

  return segments;
}
